/**
 * Wire types for model spend (TODO-2027): provider spend by origin, every
 * workload the platform spent on, and one session's spend per artefact.
 *
 * Snake-case-verbatim mirrors of Backend/app/schemas/model_spend/, held to them
 * by Backend/tests/contract/test_model_spend_wire_parity.py. Agent sessions and
 * the platform are billed to different accounts, so every response keeps the
 * two apart and a total is only ever a sum of what each priced.
 */

import type { CostAttribution } from './costAttribution'

/** Whose bill a figure is on: agent sessions', the platform's own, or both summed. */
export type SpendOrigin = 'agent_sessions' | 'platform' | 'all'

/** One origin's provider spend, with the coverage it was priced under. */
export interface OriginSpend extends CostAttribution {
  origin: SpendOrigin
  /** Token events for agent sessions, calls for the platform. */
  events: number
  prompt_tokens: number
  output_tokens: number
}

/** Work done on local hardware: counted, never priced. */
export interface LocalCallVolume {
  calls: number
  prompt_tokens: number
  output_tokens: number
}

/** The spend headline and its first split. */
export interface ProviderSpendResponse {
  start_time: string | null
  end_time: string | null
  total: OriginSpend
  origins: OriginSpend[]
  platform_local: LocalCallVolume
  /** When the platform's call record begins; spend before it was never recorded per call. */
  platform_recorded_since: string | null
}

/** Why the platform made a call. */
export type CallPurpose = 'production' | 'evaluation' | 'probe' | 'unattributed'

/** What the provider was asked to do. */
export type CallOperation = 'chat_completion' | 'embedding'

/** Whether a call left the host it was made on. */
export type CallEgressClass = 'local_only' | 'online'

/** A capability, an embedding use or a probe, under one purpose and egress. */
export interface WorkloadSpend extends CostAttribution {
  purpose: CallPurpose
  /** The capability key the calls served, else the kind of work embedded, else the purpose. */
  workload: string
  operation: CallOperation
  egress_class: CallEgressClass
  calls: number
  failed_calls: number
  usage_unreported_calls: number
  uncached_input_tokens: number
  cached_input_tokens: number
  output_tokens: number
  reasoning_tokens: number
  /** Unpriced calls counted by the reason the catalogue gave them no price. */
  unpriced_reasons: Record<string, number>
}

/**
 * The workloads in a window, with the read's own totals: what providers billed
 * for their online calls, judged by the same coverage rule as each row so a
 * reader never sums rows into a figure of its own, and the local work nobody billed.
 */
export interface WorkloadSpendResponse {
  start_time: string | null
  end_time: string | null
  provider_total: OriginSpend
  local: LocalCallVolume
  workloads: WorkloadSpend[]
}

/** One chunk, range or overview of a session, with every call made for it, retries included. */
export interface SubjectSpend extends CostAttribution {
  capability_key: string | null
  subject_kind: string | null
  subject_id: string | null
  calls: number
  failed_calls: number
  uncached_input_tokens: number
  cached_input_tokens: number
  output_tokens: number
  reasoning_tokens: number
  duration_ms: number
}

export interface SessionSpendResponse {
  session_id: string
  subjects: SubjectSpend[]
}

/**
 * The one broadcast the call record emits to the global room. Exported so the
 * consuming hook's constant and the roster pin share one list.
 */
export const MODEL_SPEND_EVENTS = ['model_spend_recorded'] as const

export type ModelSpendEventName = (typeof MODEL_SPEND_EVENTS)[number]

/** Identity only, never a figure: each client re-reads spend under its own scope. */
export interface ModelSpendRecordedPayload {
  workloads: string[]
}

/** The filters every spend read takes; an absent bound leaves that end open. */
export interface ModelSpendWindow {
  project_id?: string
  start_time?: string
  end_time?: string
}
